import {
    normalizeMeasureAttributes,
    measureAttributeDefinitions,
    resolveMeasureDefinition,
    constructionSystems,
    MeasureData as TransformedToMeasureData,
} from "@treegram/viewer-core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheckDouble } from "@fortawesome/pro-solid-svg-icons";
import { faPen, faTrash } from "@fortawesome/pro-regular-svg-icons";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import measureShapes from "./measure-shapes";
import MeasureSection from "./measure-section";
import Select from "@/components/select";

import useStoreyMeasuresContext from "../context/measures/use-storey-measures-context.ts";

import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import Button from "@/components/buttons/button";
import Card from "@/components/card";

import { Measure3dViewerRef } from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/measure-3d-viewer-container.tsx";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import usesCentimeters from "@/utils/measures/converters.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import Tooltip from "@/components/tooltip";
import removeDoubleSpaces from "@/utils/text/remove-double-space.ts";
import { ModelingType } from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import { EditingProps } from "@/pages/takeoff/layouts/measures/layouts/storey-measures/hooks/use-storey-measures-editing-tool.ts";

interface MeasureDetailsCardProps {
    setMeasureTypeIdToEdit: (measureType: string) => void;
    handleSelectMeasureType: (measureTypeId: string) => void;

    viewerRef: React.RefObject<Measure3dViewerRef | null>;
    getTransformedToMeasureDataFromGeometryId?: (geometryId: string) => TransformedToMeasureData | undefined;

    handleSave: () => Promise<boolean | void>;

    startDrawing: (measureType: MeasureType) => Promise<void>;
    startEditing: (
        editingType: ModelingType,
        isSnappingEnabled: boolean,
        editingProps: EditingProps,
    ) => Promise<void>;
}

const MeasureDetailsCard: React.FC<MeasureDetailsCardProps> = ({
    setMeasureTypeIdToEdit,
    handleSelectMeasureType,
    viewerRef,
    getTransformedToMeasureDataFromGeometryId,
    handleSave,
    startDrawing,
    startEditing,
}) => {
    const { canEdit, ensureCanEdit } = useProjectEditAccess();
    const { t } = useTranslation();

    const {
        projectMeasureTypes,
        getDrawingTypeFromMeasureTypeId,
        getDrawingMeasureRepresentationFromMeasureTypeId,
    } = useProjectMeasureContext();
    const { selectedMeasures, measures, editMeasure, removeMeasure } = useStoreyMeasuresContext();

    const selectedMeasure = selectedMeasures?.measures[0];
    const isSingleViewerSelection =
        selectedMeasures?.origin === "viewer" && selectedMeasures.measures.length === 1;

    const clonedMeasure = useMemo(() => selectedMeasure?.clone(), [selectedMeasure]);

    const originalMeasureTypeId = useMemo(() => selectedMeasure?.measureTypeId, [selectedMeasure]);

    const types = useMemo(() => {
        const originalType = projectMeasureTypes.find((pmt) => pmt.measureTypeId === originalMeasureTypeId);
        if (!originalType) return [];
        return projectMeasureTypes
            .filter((mt) => mt.measureRepresentationId === originalType.measureRepresentationId)
            .sort((a, b) =>
                removeDoubleSpaces(a.name).localeCompare(removeDoubleSpaces(b.name), "fr", {
                    sensitivity: "base",
                    numeric: true,
                }),
            );
    }, [projectMeasureTypes, originalMeasureTypeId]);

    const preSelectedItem = useMemo(
        () => projectMeasureTypes.find((mt) => mt.measureTypeId === originalMeasureTypeId),
        [originalMeasureTypeId, projectMeasureTypes],
    );

    const [selectedItem, setSelectedItem] = useState(preSelectedItem);

    useEffect(() => {
        setSelectedItem(preSelectedItem);
    }, [preSelectedItem]);

    const dataEntries = useMemo(() => {
        const records = normalizeMeasureAttributes(
            selectedItem?.measureRepresentationId ?? "",
            clonedMeasure?.dataInformations,
        );
        if (records == null) return undefined;

        if (typeof records === "object" && !Array.isArray(records) && !(records instanceof Map)) {
            return Object.entries(records as Record<string, unknown>);
        }

        return undefined;
    }, [clonedMeasure?.dataInformations, selectedItem?.measureRepresentationId]);

    const applyChanges = useCallback(async () => {
        if (!ensureCanEdit()) {
            return;
        }

        if (selectedMeasure?.measureId == null || clonedMeasure == null) return;
        editMeasure(selectedMeasure.measureId, clonedMeasure);
        await handleSave();
    }, [ensureCanEdit, selectedMeasure?.measureId, clonedMeasure, editMeasure, handleSave]);

    const dataFormatTypeEntries = useMemo(() => {
        if (!selectedItem) return [];

        const records = normalizeMeasureAttributes(
            selectedItem.measureRepresentationId,
            selectedItem.dataInformations,
        );
        return Object.keys(selectedItem.dataFormatType ?? {}).flatMap((key) =>
            records[key] == null ? [] : [[key, records[key]] as [string, unknown]],
        );
    }, [selectedItem]);

    const instanceOverrides = useMemo(() => {
        if (!selectedItem) return [];

        const typeValues = new Map(dataFormatTypeEntries);
        const typeKeys = new Set(Object.keys(selectedItem.dataFormatType ?? {}));
        return (dataEntries ?? []).filter(
            ([key, value]) => typeKeys.has(key) && value != null && typeValues.get(key) !== value,
        );
    }, [dataEntries, dataFormatTypeEntries, selectedItem]);

    const dataFormatInstanceEntries = useMemo(() => {
        if (dataEntries == null || selectedItem?.dataFormatInstance == null) return [];

        const keys = Object.keys(selectedItem.dataFormatInstance);

        return keys.flatMap((key) => dataEntries.filter(([entryKey]) => entryKey === key));
    }, [dataEntries, selectedItem?.dataFormatInstance]);

    const changeMeasure = useCallback(
        async (selected: MeasureType | null | undefined) => {
            if (!ensureCanEdit()) {
                return;
            }

            if (selected == null || clonedMeasure == null) return;

            clonedMeasure.setMeasureTypeId(selected.measureTypeId);
            setSelectedItem(selected);
            await applyChanges();
        },
        [ensureCanEdit, clonedMeasure, applyChanges],
    );

    const formatValue = useCallback((key: string, value: unknown): string => {
        if (typeof value !== "number") {
            return String(value);
        }

        let valueToFormat = Number(value);
        const useCentimeters = usesCentimeters(key);
        if (useCentimeters) valueToFormat *= 100;

        return valueToFormat.toLocaleString("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: useCentimeters ? 0 : 2,
        });
    }, []);

    const renderAttributes = (entries: [string, unknown][]) => (
        <dl className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 text-xs">
            {entries.map(([key, value]) => {
                const definition = measureAttributeDefinitions[key];
                const unit = usesCentimeters(key) ? "cm" : (definition?.unit ?? "");
                let content: React.ReactNode = formatValue(key, value);
                if (key === "alpha") content = `${Math.round(Number(value) * 100)} %`;
                if (key === "color")
                    content = (
                        <span
                            className="inline-block size-4 rounded-sm border border-gray-300 align-middle"
                            style={{ backgroundColor: String(value) }}
                            title={String(value)}
                        />
                    );
                if (key === "symbol")
                    content =
                        (
                            {
                                circle: "Cercle",
                                square: "Carré",
                                diamond: "Losange",
                            } as Record<string, string>
                        )[String(value)] ?? String(value);
                return (
                    <React.Fragment key={key}>
                        <dt className="text-gray-600">
                            {definition?.label ?? t(key)}
                            {unit && ` (${unit})`}
                        </dt>
                        <dd className="text-right tabular-nums text-gray-900">{content}</dd>
                    </React.Fragment>
                );
            })}
        </dl>
    );

    const handleStartDrawing = useCallback(async () => {
        if (!selectedMeasure) return;
        const measureType = projectMeasureTypes.find(
            (mt) => mt.measureTypeId === selectedMeasure.measureTypeId,
        );
        if (!measureType) return;

        await startDrawing(measureType);
    }, [selectedMeasure, projectMeasureTypes, startDrawing]);

    const handleEditMeasure = useCallback(() => {
        if (!selectedMeasure || !getTransformedToMeasureDataFromGeometryId) return;
        const geometryId = selectedMeasure.geometryRepresentations[0]?.geometryId;
        if (!geometryId) return;

        const measureData = getTransformedToMeasureDataFromGeometryId(geometryId);
        if (!measureData) return;

        const editingType = getDrawingTypeFromMeasureTypeId(selectedMeasure.measureTypeId);
        startEditing(editingType, false, {
            measure: selectedMeasure,
            editingPoints: measureData.points,
            editingColor: measureData.color,
            editingAlpha: measureData.alpha,
            editingMeasureRepresentation: getDrawingMeasureRepresentationFromMeasureTypeId(
                selectedMeasure.measureTypeId,
            ),
            editingDataInformations: measureData.dataInformations,
        });
    }, [
        selectedMeasure,
        getTransformedToMeasureDataFromGeometryId,
        getDrawingTypeFromMeasureTypeId,
        startEditing,
        getDrawingMeasureRepresentationFromMeasureTypeId,
    ]);

    if (selectedMeasures == null || selectedMeasures.measures.length === 0) return null;

    return (
        <>
            <Tooltip id="measure-details-card-tooltip" className="z-60" />
            <div className="flex flex-col gap-y-2 pointer-events-auto mt-auto">
                {isSingleViewerSelection && selectedMeasure != null && (
                    <Card additionalContentClasses="py-2 px-4 text-sm flex flex-col gap-y-1">
                        <div className="flex flex-row items-center w-full gap-x-px">
                            <span className="flex-1">Métré sélectionné</span>

                            <ProjectEditButton
                                data-tooltip-id="measure-details-card-tooltip"
                                data-tooltip-content="Editer le métré sélectionné"
                                variant="ghost"
                                size="3xs"
                                onClick={handleEditMeasure}
                            >
                                <FontAwesomeIcon icon={faPen} className="text-gray-700" />
                            </ProjectEditButton>

                            <ProjectEditButton
                                data-tooltip-id="measure-details-card-tooltip"
                                data-tooltip-content="Ajouter un métré de ce type"
                                variant="ghost"
                                size="3xs"
                                onClick={handleStartDrawing}
                            >
                                <FontAwesomeIcon icon={faPlus} className="text-green-500" />
                            </ProjectEditButton>

                            <ProjectEditButton
                                data-tooltip-id="measure-details-card-tooltip"
                                data-tooltip-content="Supprimer le métré sélectionné"
                                variant="ghost"
                                size="3xs"
                                onClick={() => {
                                    viewerRef.current?.removeMeasure(selectedMeasure.measureId);
                                    removeMeasure(selectedMeasure.measureId);
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} className="text-red-500" />
                            </ProjectEditButton>
                        </div>

                        <div className="flex flex-col gap-y-1.5 pt-1">
                            <div
                                className={`flex flex-row items-center w-full gap-x-2 text-xs pb-2 ${dataFormatInstanceEntries.length !== 0 ? "border-b" : ""}`}
                            >
                                <div>Type :</div>
                                <Select isDisabled={!canEdit}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.measureTypeId}
                                    options={types}
                                    value={selectedItem}
                                    isSearchable={false}
                                    menuPlacement="auto"
                                    classNames={{
                                        container: () => "flex-1 h-6",
                                        control: () => "!min-h-0 !rounded-md !border-gray-200 h-6",
                                        valueContainer: () => "!py-0 !px-2",
                                        singleValue: () => "text-xs !m-0",
                                        placeholder: () => "text-xs !m-0",
                                        input: () => "!m-0 !p-0",
                                        indicatorSeparator: () => "!h-3 !my-auto",
                                        dropdownIndicator: () => "!p-1 [&_svg]:!size-3",
                                        indicatorsContainer: () => "h-6",
                                        menu: () => "text-xs",
                                        menuList: () => "text-xs",
                                    }}
                                    onChange={(selected) => changeMeasure(selected)}
                                ></Select>
                            </div>

                            {renderAttributes(dataFormatInstanceEntries)}
                            {instanceOverrides.length > 0 && (
                                <MeasureSection
                                    key={selectedMeasure.measureId}
                                    title="Valeurs propres au métré"
                                    summary={`${instanceOverrides.length}`}
                                >
                                    {renderAttributes(instanceOverrides)}
                                </MeasureSection>
                            )}
                        </div>
                    </Card>
                )}

                <Card additionalContentClasses="flex flex-col text-sm">
                    <div className="flex flex-col py-2 px-4 gap-y-2">
                        <div className="flex flex-row items-center w-full gap-x-px">
                            <div className="flex-1 font-medium">Type de métré</div>

                            <Button
                                size="3xs"
                                variant="ghost"
                                data-tooltip-id="measure-details-card-tooltip"
                                data-tooltip-content="Sélectionner tous les objets de ce type de métré"
                                onClick={() => {
                                    if (selectedItem) handleSelectMeasureType(selectedItem.measureTypeId);
                                }}
                            >
                                <FontAwesomeIcon icon={faCheckDouble} />
                            </Button>

                            <ProjectEditButton
                                variant="ghost"
                                size="3xs"
                                data-tooltip-id="measure-details-card-tooltip"
                                data-tooltip-content="Editer ce type de métré"
                                onClick={() => {
                                    if (selectedItem) {
                                        setMeasureTypeIdToEdit(selectedItem.measureTypeId);
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faPen} />
                            </ProjectEditButton>
                        </div>

                        <div className="flex items-center justify-between gap-2 text-xs">
                            <span className="truncate font-medium" title={selectedItem?.name}>
                                {selectedItem?.name}
                            </span>
                            <span className="shrink-0 text-gray-500">
                                {
                                    measures.filter(
                                        (item) => item.measureTypeId === selectedItem?.measureTypeId,
                                    ).length
                                }{" "}
                                sur ce niveau
                            </span>
                        </div>
                        {(["representation", "business"] as const).map((role) => {
                            const definition = resolveMeasureDefinition(
                                selectedItem?.measureRepresentationId ?? "",
                                selectedItem?.dataInformations,
                            );
                            const entries = dataFormatTypeEntries.filter(
                                ([key]) => measureAttributeDefinitions[key]?.role === role,
                            );
                            const businessSummary =
                                definition.system === "none"
                                    ? "Sans système constructif"
                                    : constructionSystems[definition.system].label;
                            const summary =
                                role === "representation" ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        {measureShapes.find((item) => item.value === definition.shape)?.label}
                                        <span
                                            className="size-3 rounded-sm border border-gray-300"
                                            style={{
                                                backgroundColor: String(
                                                    selectedItem?.dataInformations?.color ?? "#3B82F6",
                                                ),
                                            }}
                                        />
                                        {Math.round(Number(selectedItem?.dataInformations?.alpha ?? 1) * 100)}{" "}
                                        %
                                    </span>
                                ) : (
                                    businessSummary
                                );
                            return (
                                <MeasureSection
                                    key={`${selectedItem?.measureTypeId}:${role}`}
                                    title={
                                        role === "representation"
                                            ? "Représentation"
                                            : "Caractéristiques métier"
                                    }
                                    summary={summary}
                                    defaultOpen={role === "business" && definition.system !== "none"}
                                >
                                    {entries.length > 0 ? (
                                        renderAttributes(entries)
                                    ) : (
                                        <p className="text-xs text-gray-500">Sans système constructif.</p>
                                    )}
                                </MeasureSection>
                            );
                        })}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default MeasureDetailsCard;
